import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  type AgentBreakdown,
  AgentChart,
} from "@/components/partner-dashboard/agent-chart"
import {
  RevenueChart,
  type RevenueChartData,
} from "@/components/partner-dashboard/revenue-chart"
import { StatCard } from "@/components/partner-dashboard/stat-card"
import {
  type TopAccount,
  TopAccountsTable,
} from "@/components/partner-dashboard/top-accounts-table"
import PartnerLayout from "@/layouts/partner-layout"
import { formatCurrency } from "@/lib/utils"
import { partnerDashboardPath } from "@/routes"
import type { TimePeriod } from "@/types"

interface DashboardProps {
  stats: {
    mrr_cents: number
    arr_cents: number
    active_accounts_count: number
    total_accounts_count: number
    prospect_accounts_count: number
    activation_rate: number
    active_agents_count: number
    avg_agents_per_account: number
  }
  revenue_chart: RevenueChartData
  agent_breakdown: AgentBreakdown
  top_accounts: TopAccount[]
  time_period: TimePeriod
  demo_mode?: boolean
}

export default function Dashboard({
  stats,
  revenue_chart,
  agent_breakdown,
  top_accounts,
  time_period,
}: DashboardProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    time_period ?? "30_days",
  )

  function handleTimePeriodChange(period: TimePeriod) {
    setSelectedPeriod(period)
    router.visit(partnerDashboardPath(), {
      data: { time_period: period },
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <PartnerLayout>
      <Head title="Dashboard" />
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl/8 font-semibold">Dashboard</h1>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <StatCard
            title="Active Accounts"
            value={stats.active_accounts_count}
            subValue={`${stats.activation_rate}%`}
            subLabel="activation rate"
            tooltip="Accounts with at least one active agent. Activation rate is active accounts divided by engaged accounts (prospects excluded)."
          />
          <StatCard
            title="Active Agents"
            value={stats.active_agents_count}
            subValue={stats.avg_agents_per_account}
            subLabel="avg per account"
            tooltip="Agents currently deployed and running across all active accounts."
          />
          <StatCard
            title="MRR"
            value={formatCurrency(stats.mrr_cents / 100)}
            subValue={formatCurrency(stats.arr_cents / 100)}
            subLabel="ARR"
            tooltip="Monthly recurring retail revenue from active agent subscriptions. ARR is MRR × 12."
          />
        </div>

        <RevenueChart
          data={revenue_chart}
          timePeriod={selectedPeriod}
          onTimePeriodChange={handleTimePeriodChange}
        />

        <div className="grid gap-6 lg:grid-cols-2">
          <AgentChart breakdown={agent_breakdown} />
          <TopAccountsTable accounts={top_accounts} />
        </div>
      </div>
    </PartnerLayout>
  )
}
